// screens/ResetPassword/ResetPassword.tsx
import React, { useState } from "react";
import axiosInstance from "../../common/axiosInstance";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";

const ResetPassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  // Password criteria verification
  const rules = [
    { id: "length", text: "At least 8 characters", valid: newPassword.length >= 8 },
    { id: "upper", text: "One uppercase letter (A-Z)", valid: /[A-Z]/.test(newPassword) },
    { id: "lower", text: "One lowercase letter (a-z)", valid: /[a-z]/.test(newPassword) },
    { id: "number", text: "One number (0-9)", valid: /\d/.test(newPassword) },
    {
      id: "special",
      text: "One special character (@$!%*?&)",
      valid: /[@$!%*?&]/.test(newPassword),
    },
  ];

  const validCount = rules.filter((r) => r.valid).length;

  const getStrengthInfo = () => {
    if (!newPassword) return { percent: 0, label: "", color: "#e2e8f0" };
    if (validCount <= 2) return { percent: 25, label: "Weak", color: "#ef4444" };
    if (validCount <= 3) return { percent: 50, label: "Fair", color: "#f59e0b" };
    if (validCount === 4) return { percent: 75, label: "Good", color: "#3b82f6" };
    return { percent: 100, label: "Strong", color: "#10b981" };
  };

  const strength = getStrengthInfo();

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!newPassword) {
      setError("Please enter a new password.");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post(`/api/tenant/reset-password`, {
        new_password: newPassword,
      });

      if (response.status === 200) {
        setSuccess("Password reset successfully! Redirecting...");
        if (user && setUser) {
          const updatedUser = { ...user, mustResetPassword: false };
          setUser(updatedUser);
        }
        setTimeout(() => {
          navigate("/dashboard");
        }, 1200);
      } else {
        setError("Failed to reset password. Please try again.");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to reset password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="container-fluid d-flex align-items-center justify-content-center py-5"
      style={{
        minHeight: "calc(100vh - 75px)",
        backgroundColor: "#f8fafc",
      }}
    >
      <div
        className="card border-0 shadow-sm"
        style={{
          width: "100%",
          maxWidth: "460px",
          borderRadius: "16px",
          boxShadow:
            "0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.02)",
          backgroundColor: "#ffffff",
          border: "1px solid #eef2f6",
        }}
      >
        <div className="card-body p-4 p-sm-5">
          {/* Header Icon & Title */}
          <div className="text-center mb-4">
            <div
              className="mx-auto d-flex align-items-center justify-content-center mb-3"
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #e6f7f6 0%, #d0f2ef 100%)",
                color: "#00a499",
                boxShadow: "0 4px 12px rgba(0, 164, 153, 0.15)",
              }}
            >
              <i className="anticon anticon-lock" style={{ fontSize: "24px" }} />
            </div>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: 700,
                color: "#1e293b",
                marginBottom: "6px",
                letterSpacing: "-0.01em",
              }}
            >
              Reset Password
            </h2>
            <p
              style={{
                fontSize: "14px",
                color: "#64748b",
                marginBottom: 0,
              }}
            >
              Please enter and confirm your new password below
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div
              className="alert alert-danger d-flex align-items-center mb-4"
              role="alert"
              style={{
                borderRadius: "10px",
                fontSize: "13px",
                padding: "12px 14px",
                border: "1px solid #fecaca",
                backgroundColor: "#fef2f2",
                color: "#991b1b",
              }}
            >
              <i
                className="anticon anticon-close-circle mr-2"
                style={{ fontSize: "16px", color: "#ef4444" }}
              />
              <span className="ml-1">{error}</span>
            </div>
          )}

          {/* Success Alert */}
          {success && (
            <div
              className="alert alert-success d-flex align-items-center mb-4"
              role="alert"
              style={{
                borderRadius: "10px",
                fontSize: "13px",
                padding: "12px 14px",
                border: "1px solid #bbf7d0",
                backgroundColor: "#f0fdf4",
                color: "#166534",
              }}
            >
              <i
                className="anticon anticon-check-circle mr-2"
                style={{ fontSize: "16px", color: "#10b981" }}
              />
              <span className="ml-1">{success}</span>
            </div>
          )}

          <form onSubmit={handleResetPassword}>
            {/* New Password Field */}
            <div className="form-group mb-3">
              <label
                htmlFor="newPassword"
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#334155",
                  marginBottom: "6px",
                  display: "block",
                }}
              >
                New Password
              </label>
              <div className="input-affix position-relative">
                <i
                  className="prefix-icon anticon anticon-lock"
                  style={{ color: "#94a3b8", fontSize: "15px" }}
                />
                <input
                  type={showNewPassword ? "text" : "password"}
                  className="form-control"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter New Password"
                  disabled={loading}
                  autoComplete="new-password"
                  style={{
                    height: "44px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "14px",
                    paddingLeft: "38px",
                    paddingRight: "42px",
                  }}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  title={showNewPassword ? "Hide password" : "Show password"}
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                  className="suffix-icon"
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    color: showNewPassword ? "#00a499" : "#94a3b8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "color 0.2s ease",
                    zIndex: 991,
                  }}
                >
                  <i
                    className={`anticon ${
                      showNewPassword ? "anticon-eye" : "anticon-eye-invisible"
                    }`}
                    style={{ fontSize: "16px" }}
                  />
                </button>
              </div>

              {/* Password Strength Indicator */}
              {newPassword.length > 0 && (
                <div className="mt-2">
                  <div
                    className="d-flex justify-content-between align-items-center mb-1"
                    style={{ fontSize: "12px" }}
                  >
                    <span style={{ color: "#64748b" }}>Password strength</span>
                    <span
                      style={{
                        fontWeight: 600,
                        color: strength.color,
                      }}
                    >
                      {strength.label}
                    </span>
                  </div>
                  <div
                    style={{
                      height: "4px",
                      backgroundColor: "#e2e8f0",
                      borderRadius: "2px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${strength.percent}%`,
                        backgroundColor: strength.color,
                        transition: "all 0.3s ease",
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="form-group mb-4">
              <label
                htmlFor="confirmPassword"
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#334155",
                  marginBottom: "6px",
                  display: "block",
                }}
              >
                Confirm Password
              </label>
              <div className="input-affix position-relative">
                <i
                  className="prefix-icon anticon anticon-lock"
                  style={{ color: "#94a3b8", fontSize: "15px" }}
                />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="form-control"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm New Password"
                  disabled={loading}
                  autoComplete="new-password"
                  style={{
                    height: "44px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "14px",
                    paddingLeft: "38px",
                    paddingRight: "42px",
                  }}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  title={showConfirmPassword ? "Hide password" : "Show password"}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  className="suffix-icon"
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    color: showConfirmPassword ? "#00a499" : "#94a3b8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "color 0.2s ease",
                    zIndex: 991,
                  }}
                >
                  <i
                    className={`anticon ${
                      showConfirmPassword ? "anticon-eye" : "anticon-eye-invisible"
                    }`}
                    style={{ fontSize: "16px" }}
                  />
                </button>
              </div>

              {/* Match indicator */}
              {confirmPassword.length > 0 && (
                <div
                  className="d-flex align-items-center mt-1"
                  style={{
                    fontSize: "12px",
                    color:
                      newPassword === confirmPassword ? "#10b981" : "#ef4444",
                    fontWeight: 500,
                  }}
                >
                  <i
                    className={`anticon ${
                      newPassword === confirmPassword
                        ? "anticon-check-circle"
                        : "anticon-close-circle"
                    } mr-1`}
                  />
                  <span className="ml-1">
                    {newPassword === confirmPassword
                      ? "Passwords match"
                      : "Passwords do not match"}
                  </span>
                </div>
              )}
            </div>

            {/* Password Requirements Checklist */}
            <div
              className="p-3 mb-4"
              style={{
                backgroundColor: "#f8fafc",
                borderRadius: "10px",
                border: "1px solid #f1f5f9",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#475569",
                  marginBottom: "8px",
                }}
              >
                Password requirements:
              </div>
              <ul className="list-unstyled mb-0" style={{ fontSize: "12px" }}>
                {rules.map((rule) => (
                  <li
                    key={rule.id}
                    className="d-flex align-items-center mb-1"
                    style={{
                      color: rule.valid ? "#10b981" : "#64748b",
                      transition: "color 0.2s ease",
                    }}
                  >
                    <i
                      className={`anticon ${
                        rule.valid
                          ? "anticon-check-circle text-success"
                          : "anticon-minus-circle text-muted"
                      } mr-2`}
                      style={{ fontSize: "13px" }}
                    />
                    <span className="ml-1">{rule.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="btn width100 d-flex align-items-center justify-content-center"
              disabled={loading || !newPassword || !confirmPassword}
              style={{
                height: "46px",
                backgroundColor: "#00a499",
                borderColor: "#00a499",
                color: "#ffffff",
                fontWeight: 600,
                fontSize: "15px",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0, 164, 153, 0.25)",
                transition: "all 0.2s ease",
                cursor:
                  loading || !newPassword || !confirmPassword
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  loading || !newPassword || !confirmPassword ? 0.7 : 1,
              }}
              onMouseEnter={(e) => {
                if (!loading && newPassword && confirmPassword) {
                  e.currentTarget.style.backgroundColor = "#008a80";
                  e.currentTarget.style.borderColor = "#008a80";
                  e.currentTarget.style.boxShadow =
                    "0 6px 16px rgba(0, 164, 153, 0.35)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#00a499";
                e.currentTarget.style.borderColor = "#00a499";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 164, 153, 0.25)";
              }}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm mr-2"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="ml-1">Resetting Password...</span>
                </>
              ) : (
                "Reset Password"
              )}
            </button>
          </form>

          {/* Security footnote */}
          <div
            className="text-center mt-4 d-flex align-items-center justify-content-center"
            style={{
              fontSize: "12px",
              color: "#94a3b8",
            }}
          >
            <i className="anticon anticon-safety mr-1" />
            <span className="ml-1">Protected by secure encryption</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
